import request from '@/utils/http';
import { storageGetItem } from '@/utils/storage';
import { prefix } from '@/utils/prefix';
import { unwrapResponse } from '@/utils/http/types';
import { encode } from '@/utils/jsencrypt';

/** 登录成功响应体 */

function toRawEnumChildItem(item) {
  return {
    uid: item.id,
    text: item.name,
    value: item.value,
    code: item.code ?? undefined,
    children: item.children?.map(toRawEnumChildItem),
    data: {
      ...item,
      html: ''
    }
  };
}
function toRawEnumItem(item) {
  return {
    uid: item.id,
    code: item.code ?? undefined,
    name: item.name,
    children: item.children?.map(toRawEnumChildItem) || []
  };
}

/**
 * 分页查询字典列表
 */
export async function getDictPage(params) {
  return unwrapResponse(request.get(`${prefix.iam}/dicts/page`, {
    params
  }));
}

/**
 * 获取所有枚举（字典 + 枚举项 children）
 */
export async function getAllDicts() {
  const response = await unwrapResponse(request.get(`${prefix.iam}/dicts/items/list`));
  return response.map(toRawEnumItem);
}

/**
 * 获取字典详情
 */
export async function getDict(id) {
  return unwrapResponse(request.get(`${prefix.iam}/dicts/${id}`));
}

/**
 * 保存字典（根据是否有 id 判断新增或更新）
 */
export async function saveDict(data) {
  const {
    id,
    ...rest
  } = data;
  if (id) {
    return unwrapResponse(request.put(`${prefix.iam}/dicts/${id}`, rest));
  }
  return unwrapResponse(request.post(`${prefix.iam}/dicts`, rest));
}

/**
 * 删除字典
 */
export async function deleteDict(id) {
  return unwrapResponse(request.delete(`${prefix.iam}/dicts/${id}`));
}

/**
 * 导出选中字典及其枚举项为 Excel（返回原始 Response，交给 download 处理）
 */
export async function exportDicts(ids) {
  const token = storageGetItem('token') || '';
  return fetch(`${prefix.iam}/dicts/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? {
        Authorization: `Bearer ${token}`
      } : {})
    },
    credentials: 'include',
    body: JSON.stringify({
      ids: ids.join(',')
    })
  });
}

/**
 * 导入字典 Excel（含枚举项）
 */
export async function importDicts(file) {
  const formData = new FormData();
  formData.append('file', file);
  return unwrapResponse(request.post(`${prefix.iam}/dicts/import`, formData));
}

/**
 * 查询字典根枚举项列表
 */
export async function getEnumItemList(dictId) {
  return unwrapResponse(request.get(`${prefix.iam}/enum/list`, {
    params: {
      dictId
    }
  }));
}

/**
 * 查询枚举子项列表
 */
export async function getEnumItemChildren(id) {
  return unwrapResponse(request.get(`${prefix.iam}/enum/children`, {
    params: {
      id
    }
  }));
}

/**
 * 保存枚举项（根据是否有 id 判断新增或更新）
 */
export async function saveEnumItem(data) {
  console.log(data);
  if (data.data && typeof data.data === 'string') {
    data.data = JSON.parse(data.data);
  } else {
    delete data.data;
  }
  const {
    id,
    ...rest
  } = data;
  if (id) {
    return unwrapResponse(request.put(`${prefix.iam}/enum/${id}`, rest));
  }
  return unwrapResponse(request.post(`${prefix.iam}/enum`, rest));
}

/**
 * 删除枚举项
 */
export async function deleteEnumItem(id) {
  return unwrapResponse(request.delete(`${prefix.iam}/enum/${id}`));
}

/**
 * 获取部门树
 */
export async function getDepartmentTree() {
  return unwrapResponse(request.get(`${prefix.iam}/department/tree`));
}

/**
 * 获取部门详情
 */
export async function getDepartment(id) {
  return unwrapResponse(request.get(`${prefix.iam}/department/${id}`));
}

/**
 * 保存部门（根据是否有 id 判断新增或更新）
 */
export async function saveDepartment(data) {
  if (data.id) {
    const {
      id,
      ...rest
    } = data;
    return unwrapResponse(request.patch(`${prefix.iam}/department/${id}`, rest));
  }
  return unwrapResponse(request.post(`${prefix.iam}/department`, data));
}

/**
 * 删除部门
 */
export async function deleteDepartment(id) {
  return unwrapResponse(request.delete(`${prefix.iam}/department/${id}`));
}

/**
 * 查询部门预设人员列表
 */
export async function getDepartmentPresetUsers(deptId) {
  return unwrapResponse(request.get(`${prefix.iam}/department/${deptId}/preset`));
}

/**
 * 新增部门预设人员
 */
export async function createDepartmentPresetUsers(data) {
  return unwrapResponse(request.post(`${prefix.iam}/department/preset`, data));
}

/**
 * 编辑部门预设人员
 */
export async function updateDepartmentPresetUsers(id, data) {
  return unwrapResponse(request.put(`${prefix.iam}/department/preset/${id}`, data));
}

/**
 * 删除部门预设人员
 */
export async function deleteDepartmentPresetUser(id) {
  return unwrapResponse(request.delete(`${prefix.iam}/department/preset/${id}`));
}

/**
 * 查询菜单角色列表
 */
export async function getMenuRoleList(params) {
  return unwrapResponse(request.get(`${prefix.iam}/menu/list`, {
    params
  }));
}

/**
 * 菜单分配角色
 */
export async function assignMenuRoles(menuKey, data) {
  const encodedMenuKey = encodeURIComponent(menuKey);
  return unwrapResponse(request.put(`${prefix.iam}/menu/${encodedMenuKey}/roles`, data));
}

/**
 * 删除菜单中的角色
 */
export async function deleteMenuRole(id) {
  return unwrapResponse(request.delete(`${prefix.iam}/menu/role/${id}`));
}

/**
 * 批量删除菜单中的角色
 */
export async function deleteMenuRoles(ids) {
  return unwrapResponse(request.delete(`${prefix.iam}/menu/roles`, {
    params: {
      ids: ids.join(',')
    }
  }));
}

/**
 * 查询菜单权限列表
 */
export async function getMenuPermissions(menuKey) {
  return unwrapResponse(request.get(`${prefix.iam}/menu/permission/list`, {
    params: {
      menuKey
    }
  }));
}

/**
 * 查询角色对应菜单的权限列表（带 grant）
 */
export async function getRolePermissions(roleId, menuKey) {
  return unwrapResponse(request.get(`${prefix.iam}/menu/role-permission/list`, {
    params: {
      roleId,
      menuKey
    }
  }));
}

/**
 * 授予角色操作权限
 */
export async function grantRolePermission(roleId, permissionId) {
  return unwrapResponse(request.post(`${prefix.iam}/menu/role-permission/grant`, {
    roleId,
    permissionId
  }));
}

/**
 * 撤销角色操作权限
 */
export async function revokeRolePermission(roleId, permissionId) {
  return unwrapResponse(request.post(`${prefix.iam}/menu/role-permission/revoke`, {
    roleId,
    permissionId
  }));
}

/**
 * 创建菜单权限
 */
export async function createMenuPermission(data) {
  return unwrapResponse(request.post(`${prefix.iam}/menu/permission`, data));
}

/**
 * 编辑菜单权限
 */
export async function updateMenuPermission(id, data) {
  return unwrapResponse(request.put(`${prefix.iam}/menu/permission/${id}`, data));
}

/**
 * 删除菜单权限
 */
export async function deleteMenuPermission(id) {
  return unwrapResponse(request.delete(`${prefix.iam}/menu/permission/${id}`));
}

/**
 * 根据userId查询用户可查看的菜单key列表
 */
export async function getUserMenuKeys(userId) {
  return unwrapResponse(request.get(`${prefix.iam}/menu/user/keys`, {
    params: {
      userId
    }
  }));
}

/**
 * 根据userId和menuKey查询当前页面的操作权限编码
 */
export async function getUserPermissionCodes(userId, menuKey) {
  return unwrapResponse(request.get(`${prefix.iam}/menu/permission/codes`, {
    params: {
      userId,
      menuKey
    }
  }));
}

/**
 * 分页查询岗位列表
 */
export async function getPositionPage(params) {
  return unwrapResponse(request.get(`${prefix.iam}/position/page`, {
    params
  }));
}

/**
 * 保存岗位（根据是否有 id 判断新增或更新）
 */
export async function savePosition(data) {
  if (data.id) {
    const {
      id,
      ...rest
    } = data;
    return unwrapResponse(request.patch(`${prefix.iam}/position/${id}`, rest));
  }
  return unwrapResponse(request.post(`${prefix.iam}/position`, data));
}

/**
 * 删除岗位
 */
export async function deletePosition(id) {
  return unwrapResponse(request.delete(`${prefix.iam}/position/${id}`));
}

/**
 * 分页查询角色列表
 */
export async function getRolePage(params) {
  return unwrapResponse(request.get(`${prefix.iam}/role/page`, {
    params
  }));
}

/**
 * 获取角色列表
 */
export async function getRoleList() {
  return unwrapResponse(request.get(`${prefix.iam}/role/list`));
}

/**
 * 获取角色详情
 */
export async function getRole(id) {
  return unwrapResponse(request.get(`${prefix.iam}/role/${id}`));
}

/**
 * 获取角色直接继承角色列表
 */
export async function getDirectInheritedRoleList(id) {
  return unwrapResponse(request.get(`${prefix.iam}/role/${id}/inherits`));
}

/**
 * 获取角色继承树
 */
export async function getRoleInheritTree(id) {
  return unwrapResponse(request.get(`${prefix.iam}/role/${id}/inherits/tree`));
}

/**
 * 获取角色授予的人员列表
 */
export async function getGrantedPersons(id) {
  return unwrapResponse(request.get(`${prefix.iam}/role/${id}/persons`));
}

/**
 * 设置角色继承关系
 */
export async function updateRoleInherits(id, data) {
  return unwrapResponse(request.put(`${prefix.iam}/role/${id}/inherits`, data));
}

/**
 * 保存角色（根据是否有 id 判断新增或更新）
 */
export async function saveRole(data) {
  const {
    id,
    ...rest
  } = data;
  if (id) {
    return unwrapResponse(request.patch(`${prefix.iam}/role/${id}`, rest));
  }
  return unwrapResponse(request.post(`${prefix.iam}/role`, rest));
}

/**
 * 删除角色
 */
export async function deleteRole(id) {
  return unwrapResponse(request.delete(`${prefix.iam}/role/${id}`));
}

/**
 * 获取人员列表
 */
export async function getPersonPage(params) {
  return unwrapResponse(request.get(`${prefix.iam}/person/page`, {
    params
  }));
}

/**
 * 获取人员详情
 */
export async function getPerson(id) {
  return unwrapResponse(request.get(`${prefix.iam}/person/${id}`));
}

/**
 * 获取人员岗位历史
 */
export async function getPersonPositionHistory(id) {
  return unwrapResponse(request.get(`${prefix.iam}/person/${id}/positions/history`));
}

/**
 * 保存人员（根据是否有 id 判断新增或更新）
 */
export async function savePerson(data) {
  if (data.id) {
    const {
      id,
      ...rest
    } = data;
    return unwrapResponse(request.patch(`${prefix.iam}/person/${id}`, rest));
  }
  return unwrapResponse(request.post(`${prefix.iam}/person`, data));
}

/**
 * 删除人员
 */
export async function deletePerson(id) {
  return unwrapResponse(request.delete(`${prefix.iam}/person/${id}`));
}

/**
 * 设置人员岗位
 */
export async function setPersonPosition(id, data) {
  return unwrapResponse(request.put(`${prefix.iam}/person/${id}/position`, data));
}

/**
 * 创建用户
 */
export async function createUser(data) {
  return unwrapResponse(request.post(`${prefix.iam}/user`, data));
}

/** 公钥响应体 */

/** 修改头像响应体 */

/**
 * 获取公钥
 */
export async function getPublicKey() {
  const result = await unwrapResponse(request.post(`${prefix.iam}/auth/public-key`));
  return result.publicKey;
}

/**
 * 用户登录
 */
export async function sysLogin(username, password, publicKey) {
  return unwrapResponse(request.post(`${prefix.iam}/auth/login`, {
    username,
    password: encode(password, publicKey)
  }));
}

/**
 * 分页查询用户列表
 */
export async function getUserPage(params) {
  return unwrapResponse(request.get(`${prefix.iam}/user/page`, {
    params
  }));
}

/**
 * 获取用户详情
 */
export async function getUser(id) {
  return unwrapResponse(request.get(`${prefix.iam}/user/${id}`));
}

/**
 * 获取用户角色列表
 */
export async function getUserRoles(id) {
  return unwrapResponse(request.get(`${prefix.iam}/user/${id}/roles`));
}

/**
 * 设置用户角色
 */
export async function updateUserRoles(id, roleIds) {
  return unwrapResponse(request.put(`${prefix.iam}/user/${id}/roles`, {
    roleIds
  }));
}

/**
 * 更新用户状态
 */
export async function updateUserStatus(id, disabled, expireDate) {
  return unwrapResponse(request.put(`${prefix.iam}/user/${id}/status`, {
    disabled,
    expireDate
  }));
}

/**
 * 重置密码
 * @param data 包含用户ID和新密码
 */
export async function resetPassword(data, publicKey) {
  return unwrapResponse(request.post(`${prefix.iam}/auth/reset`, {
    ...data,
    password: encode(data.password, publicKey)
  }));
}
/**
 * 用户自己修改密码（需 RSA 公钥加密旧密码 + 新密码）
 */
export async function changePassword(data, publicKey) {
  return unwrapResponse(request.post(`${prefix.iam}/auth/change`, {
    userId: data.userId,
    oldPassword: encode(data.oldPassword, publicKey),
    newPassword: encode(data.newPassword, publicKey)
  }));
}

/**
 * 设置用户头像
 */
export async function setUserAvatar(file, userId) {
  const formData = new FormData();
  formData.append('photo', file);
  formData.append('userId', userId);
  return unwrapResponse(request.post(`${prefix.iam}/photo/user`, formData));
}
/**
 * 设置用户签名
 */
export async function setUserSignature(file, userId) {
  const formData = new FormData();
  formData.append('signature', file);
  formData.append('userId', userId);
  return unwrapResponse(request.post(`${prefix.iam}/signature/user`, formData));
}

// ---------------------------------------------------------------------------
// 卡片布局（POST /layout/save, GET /layout/detail）
// ---------------------------------------------------------------------------

/**
 * 保存卡片布局（存在则更新，不存在则新增）
 */
export async function saveCardLayout(data) {
  return unwrapResponse(request.post(`${prefix.iam}/layout/save`, data));
}

/**
 * 按 userId + type 查询卡片布局详情
 */
export async function getCardLayoutDetail({
  userName,
  type
}) {
  return unwrapResponse(request.get(`${prefix.iam}/layout/detail`, {
    params: {
      userName,
      type
    }
  }));
}